import React, { useContext, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetBudgetProposals } from 'routes/BudgetModule/helpers/hooks/budgetProposals';
import { useParams } from 'react-router-dom';
import { BudgetExpenseOrIncome } from 'business/constants';
import Loading from 'components/Loading';
import BudgetHeader from 'routes/BudgetModule/components/shared/BudgetHeader/BudgetHeader';
import {
    filterBudgetProposals,
    getSortedBudgetNamesWithArchivedStatusByYear,
    getExercicesByBudgetName,
    getExercicesOfBudget,
} from 'routes/BudgetModule/components/shared/BudgetLibrary/helper';
import TabulatedPage from 'ui/TabulatedPage/TabulatedPage';
import { useIntegrationSoftwares } from 'helpers/integrations';
import BudgetContent from 'routes/BudgetModule/components/shared/BudgetContent';
import { styled } from '@mui/material/styles';
import GridApiProvider from 'routes/BudgetModule/contexts/GridApiProvider';
import { useSearchParams } from 'hooks/searchParams';
import BudgetSynthesisAndRatios from './BudgetSynthesisAndRatios/BudgetSynthesisAndRatios';
import AllOperations from './AllOperations/AllOperations';
import AllInscriptions from './AllInscriptions/AllInscriptions';
import AllAps from './AllAps/AllAps';
import { isApViewSupported } from './AllAps/helper';
import AllProjections from './AllProjections/AllProjections';
import BudgetOverviewFilters from '../shared/BudgetLibrary/BudgetFilters/BudgetOverviewFilters';
import { isCustomBudgetProposal } from '../BudgetProposal/Resource/hooks/budgetProposal';
import BudgetSynthesisResult from './BudgetSynthesisAndRatios/BudgetSynthesisResult';
import AnalyseArguments from './AnalyseArguments/AnalyseArguments';
import NoDataState from 'ui/NoDataState/NoDataState';
import {
    useGetCurrentDemoFinanceSoftware,
    useHasDemoFinanceSoftwareOverride,
} from 'routes/BudgetModule/components/shared/BudgetHeader/hooks';
import { useSelectSecondaryBudgetAxisSourceTable } from 'routes/BudgetModule/components/BudgetWithProvider/SecondaryBudgetAxisSourceTableContext';
import { IntegrationContext } from 'routes/BudgetModule/components/BudgetWithProvider/IntegrationContext';
import { ALL_BUDGETS_VALUE } from 'routes/BudgetModule/components/BudgetOverview/constants';

const StyTabulatedPage = styled(TabulatedPage, {
    name: 'StyTabulatedPage',
})({
    height: '75%',
    '& .Mui-TabulatedPage-content-div': { height: '100%' },
});

const BudgetOverview = (): React.ReactElement => {
    const { t } = useTranslation();
    const {
        expenseOrIncome = BudgetExpenseOrIncome.EXPENSE.toLowerCase(),
        projectionType,
    } = useParams<{
        expenseOrIncome: string | undefined;
        projectionType: string | undefined;
    }>();
    const lastVisitedProjectionType = useRef('atterrissage');
    useEffect(() => {
        if (projectionType) {
            lastVisitedProjectionType.current = projectionType;
        }
    }, [projectionType]);

    const {
        data: budgetProposalResponse,
        isLoading: areBudgetProposalsLoading,
    } = useGetBudgetProposals();

    // SCHOOL --
    const integrations = useContext(IntegrationContext);
    const isSchool = isCustomBudgetProposal(integrations);

    const showAllBudgets = [
        '/budget_overview/finance_ratios',
        '/budget_overview/all_operations/',
    ].some((pathname) => location.pathname.startsWith(pathname));

    const budgetProposals = budgetProposalResponse || [];
    const exercices = getExercicesOfBudget(budgetProposals);
    const budgetNamesByYear =
        getSortedBudgetNamesWithArchivedStatusByYear(budgetProposals);
    const exercicesByBudgetName = getExercicesByBudgetName(budgetProposals);

    const {
        exercice: [exerciceParam, setSelectedExercice],
        budget: [selectedBudgetName, setSelectedBudgetName],
    } = useSearchParams(['exercice', 'budget']);

    // We initialize the selectedExercice as null
    // so that we can update the default filter when the data is set
    const selectedExercice = !Number.isNaN(parseInt(exerciceParam ?? '', 10))
        ? parseInt(exerciceParam ?? '', 10)
        : null;

    const budgetOptionsForSelectedExercice = selectedExercice
        ? budgetNamesByYear[selectedExercice]
        : undefined;
    const budgetNames =
        budgetOptionsForSelectedExercice?.map((budget) => budget.label) || [];
    const canUseDemoSoftwareOverride = useHasDemoFinanceSoftwareOverride();
    const { data: demoSoftwareName, isLoading: isLoadingCurrentDemoSoftware } =
        useGetCurrentDemoFinanceSoftware({
            enabled: canUseDemoSoftwareOverride,
        });
    const sourceTable = useSelectSecondaryBudgetAxisSourceTable();
    const {
        data: integratedSoftwares = new Set(),
        isLoading: areSoftwaresLoading,
    } = useIntegrationSoftwares();

    const filteredBudgetProposals = filterBudgetProposals(
        budgetProposals,
        selectedBudgetName,
        selectedExercice ? selectedExercice.toString() : 'no-exercice',
    );
    const maxExercice = Math.max(...exercices);

    const firstBudgetName = budgetNames[0];
    useEffect(() => {
        if (!exerciceParam && exercices.length > 0) {
            setSelectedExercice(maxExercice.toString());
        }

        if (!selectedBudgetName && selectedExercice) {
            setSelectedBudgetName(firstBudgetName);
        }
    }, [exerciceParam, selectedExercice, exercices.length]);

    if (
        areBudgetProposalsLoading ||
        areSoftwaresLoading ||
        isLoadingCurrentDemoSoftware
    ) {
        return (
            <div className="page-container dx-viewport">
                <Loading />
            </div>
        );
    }

    if (showAllBudgets) {
        budgetNames.unshift(ALL_BUDGETS_VALUE);
    } else if (selectedBudgetName === ALL_BUDGETS_VALUE) {
        // When entering another tab, allBudgetsOption is not relevant anymore and must be replaced
        setSelectedBudgetName(firstBudgetName);
    }

    const allInscriptionsTab = {
        label: t('budgetOverview.allInscriptions'),
        children: (
            <AllInscriptions
                budgetProposals={filteredBudgetProposals}
                selectedBudgetName={selectedBudgetName}
                year={selectedExercice ?? 0}
            />
        ),
        pathname: `/budget_overview/all_inscriptions/${expenseOrIncome}`,
        disabled: false,
    };

    const allProjectionsTab = {
        label: t('budgetOverview.allProjections'),
        children: <AllProjections budgetProposals={filteredBudgetProposals} />,
        pathname: `/budget_overview/all_projections/${expenseOrIncome}/${
            projectionType || lastVisitedProjectionType.current
        }`,
        disabled: false,
    };

    const allOperationsTab = {
        label: t('budgetOverview.allSecondaryBudgetAxis', {
            context: sourceTable,
        }),
        children: (
            <AllOperations
                filteredBudgetProposals={filteredBudgetProposals}
                allBudgetProposals={budgetProposals}
                year={
                    selectedExercice
                        ? selectedExercice.toString()
                        : 'no-exercice'
                }
                budgetName={selectedBudgetName!}
            />
        ),
        pathname: `/budget_overview/all_operations/${expenseOrIncome}`,
        disabled: false,
    };

    const budgetSynthesisTab = {
        label: t('budgetOverview.budgetSynthesisAndRatios'),
        children: (
            <BudgetSynthesisAndRatios
                budgetProposals={filteredBudgetProposals}
                selectedBudgetName={selectedBudgetName}
                year={
                    selectedExercice
                        ? selectedExercice.toString()
                        : 'no-exercice'
                }
            />
        ),
        pathname: '/budget_overview/finance_ratios',
        disabled: false,
    };

    const budgetSummaryTab = {
        label: t('budgetOverview.budgetSummaryResult'),
        children: (
            <BudgetSynthesisResult
                budgetProposals={filteredBudgetProposals}
                selectedBudgetName={selectedBudgetName}
                year={selectedExercice}
            />
        ),
        pathname: '/budget_overview/finance_ratios',
        disabled: false,
    };

    const analyseArgumentsTab = {
        label: t('budgetOverview.analyseArguments'),
        children: (
            <AnalyseArguments
                budgetProposals={filteredBudgetProposals}
                year={selectedExercice ? selectedExercice : 0}
                selectedBudgetName={selectedBudgetName}
            />
        ),
        pathname: `/budget_overview/analyse_arguments/${expenseOrIncome}`,
        disabled: false,
    };

    const tabsList = !isSchool
        ? [
              budgetSynthesisTab,
              analyseArgumentsTab,
              allInscriptionsTab,
              allOperationsTab,
              allProjectionsTab,
          ]
        : [
              budgetSummaryTab,
              analyseArgumentsTab,
              allInscriptionsTab,
              allProjectionsTab,
          ];

    if (isApViewSupported(integratedSoftwares, demoSoftwareName)) {
        tabsList.push({
            label: t('budgetOverview.allAps'),
            children: (
                <AllAps
                    year={selectedExercice ? selectedExercice.toString() : ''}
                />
            ),
            pathname: `/budget_overview/all_aps/${
                expenseOrIncome || BudgetExpenseOrIncome.EXPENSE.toLowerCase()
            }`,
            disabled: false,
        });
    }
    const hasBudgetProposals = filteredBudgetProposals.length > 0;
    return (
        <div className="page-container dx-viewport">
            <BudgetHeader
                headerTitle={t('budgetOverview.headerTitle')}
                exercice={selectedExercice}
            />
            <BudgetContent>
                {hasBudgetProposals ? (
                    <BudgetOverviewFilters
                        selectedBudgetName={selectedBudgetName}
                        setSelectedBudgetName={setSelectedBudgetName}
                        sortedBudgetNames={budgetNames}
                        exercicesByBudgetName={exercicesByBudgetName}
                        selectedExercice={selectedExercice}
                        setSelectedExercice={setSelectedExercice}
                    />
                ) : (
                    <NoDataState />
                )}
            </BudgetContent>
            {hasBudgetProposals ? (
                <GridApiProvider>
                    <StyTabulatedPage tabsList={tabsList} keepSearch />
                </GridApiProvider>
            ) : null}
        </div>
    );
};

export default BudgetOverview;
